import React, { useMemo, useState, useEffect } from "react";
import { collection, onSnapshot, query, where, orderBy, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import EventCheckout from "../../components/EventCheckout";
import "./big-event.css";

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
console.log("🔑 Stripe Publishable Key:", stripeKey ? `${stripeKey.substring(0, 20)}...` : "NOT FOUND");
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
console.log("💳 Stripe Promise:", stripePromise ? "Loaded" : "NULL - KEY MISSING!");

export default function Purchases() {
  const [range, setRange] = useState("last30");
  const [queryText, setQueryText] = useState("");
  const [eventFilter, setEventFilter] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedEventForCheckout, setSelectedEventForCheckout] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [purchases, setPurchases] = useState([]);

  // Read preselected event filter (set by BigEventBuilder → Purchases)
  useEffect(() => {
    const f = sessionStorage.getItem("purchasesEventFilter");
    setEventFilter(f || null);
  }, []);

  const dateRangeLabel = useMemo(() => {
    switch (range) {
      case "today":
        return "Today";
      case "last7":
        return "Last 7 Days";
      case "last30":
      default:
        return "Last 30 Days";
    }
  }, [range]);

  // Load events for selection
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "big_events"), (snap) => {
      const list = [];
      snap.forEach((doc) => {
        const data = doc.data();
        list.push({ id: doc.id, ...data });
      });
      // Prefer active/upcoming events first
      list.sort((a, b) => (a.status === "active" ? -1 : 1));
      setEvents(list);
    });
    return () => unsub();
  }, []);

  // Load purchases from tickets subcollection (time range)
  useEffect(() => {
    const now = new Date();
    let start = new Date(now);
    if (range === "today") {
      start.setHours(0, 0, 0, 0);
    } else if (range === "last7") {
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else {
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    const qRef = query(
      collection(db, "tickets"),
      where("purchasedAt", ">=", Timestamp.fromDate(start)),
      orderBy("purchasedAt", "desc")
    );
    const unsub = onSnapshot(qRef, (snap) => {
      // Group tickets by confirmation number (each purchase may have multiple tickets)
      const purchaseMap = new Map();
      snap.forEach((doc) => {
        const d = doc.data();
        const conf = d.confirmation;
        if (!conf) return; // Skip tickets without confirmation
        
        if (!purchaseMap.has(conf)) {
          const [first, ...rest] = (d.buyerName || "").split(" ");
          const last = rest.join(" ") || "";
          purchaseMap.set(conf, {
            id: doc.id,
            eventId: d.eventId,
            conf: conf,
            first,
            last,
            email: d.buyerEmail,
            leader: d.buyerAccountType || "",
            qnt: d.totalQuantity || 1,
            price: d.pricePerTicket || 0, // cents
            total: d.totalPurchaseAmount || 0, // cents
            regDateTs: d.purchasedAt,
          });
        }
      });
      setPurchases(Array.from(purchaseMap.values()));
    });
    return () => unsub();
  }, [range]);

  const assignedCount = 2675;
  const unassignedCount = 421;

  const handleActions = () => {
    alert(`Bulk Actions\n\nOptions:\n- Export selected to CSV\n- Send invoices\n- Reassign tickets\n- Refund purchases`);
  };

  const handleAddTicket = () => {
    alert(`Add Manual Ticket\n\nUse for:\n- Complimentary tickets\n- Offline purchases\n- Special registrations`);
  };

  const handleBuyTickets = () => {
    const ev = events.find((e) => e.id === selectedEventId);
    if (!ev) {
      alert("Please select an event first");
      return;
    }
    setSelectedEventForCheckout({
      eventId: ev.id,
      eventName: ev.title || ev.eventName || ev.name || "Event",
      organizerId: ev.organizerId || "",
      price: Number(ev.ticketPrice) || 5000,
    });
    setShowCheckout(true);
  };

  const handleSendInvoice = (purchase) => {
    alert(`Send Invoice\n\nTo: ${purchase.email}\nConfirmation: ${purchase.conf}\nTotal: $${purchase.total}\n\nThis will email the purchase receipt.`);
  };

  const handleOpenPDF = (purchase) => {
    alert(`Opening PDF Invoice\n\nConfirmation: ${purchase.conf}\nGenerated invoice for ${purchase.first} ${purchase.last}`);
  };

  const searched = useMemo(() => {
    const q = queryText.trim().toLowerCase();
    return purchases.filter((p) => {
      if (eventFilter && p.eventId !== eventFilter) return false;
      if (!q) return true;
      const blob =
        `${p.conf} ${p.first} ${p.last} ${p.email} ${p.leader} ${p.qnt} ${p.price} ${p.total}`.toLowerCase();
      return blob.includes(q);
    });
  }, [purchases, queryText, eventFilter]);

  // Fake KPI (visual)
  const kpis = {
    tickets: searched.reduce((a, b) => a + (b.qnt || 0), 0),
    collected: searched.reduce((a, b) => a + (b.total || 0), 0),
    addons: Math.round(searched.reduce((a, b) => a + (b.total || 0), 0) * 0.17),
  };

  return (
    <div className="be-wrap">
      <div className="be-header compact">
        <div>
          <h1 className="be-title">Purchases</h1>
          <p className="be-subtitle">
            {eventFilter ? (
              <>
                Filtered to selected event (
                <span className="mono">{eventFilter}</span>) — {dateRangeLabel}.
              </>
            ) : (
              <>View and manage all purchases — {dateRangeLabel}.</>
            )}
          </p>
        </div>
      </div>

      <div className="be-toolbar">
        <div className="kpi-group">
          <div className="kpi-card">
            <div className="kpi-label">Total Tickets</div>
            <div className="kpi-value">{kpis.tickets.toLocaleString()}</div>
            <div className="kpi-muted">{dateRangeLabel}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Total Collected</div>
            <div className="kpi-value">${kpis.collected.toLocaleString()}</div>
            <div className="kpi-muted">{dateRangeLabel}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Add-ons Collected</div>
            <div className="kpi-value">${kpis.addons.toLocaleString()}</div>
            <div className="kpi-muted">{dateRangeLabel}</div>
          </div>
        </div>

        <div className="assignments-and-actions">
          <div className="counts">
            <span className="count-badge green">
              Assigned: {assignedCount.toLocaleString()}
            </span>
            <span className="count-badge yellow">
              Unassigned: {unassignedCount.toLocaleString()}
            </span>
          </div>
          <div className="toolbar-actions">
            <button className="btn subtle" onClick={handleActions}>Actions</button>
            <div className="event-picker">
              <select
                className="input input-compact"
                value={selectedEventId}
                onChange={(e) => setSelectedEventId(e.target.value)}
              >
                <option value="">Select Event</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title || ev.eventName || ev.name || ev.id}
                  </option>
                ))}
              </select>
            </div>
            <button className="btn subtle" onClick={handleBuyTickets} disabled={!selectedEventId}>🎫 Buy Tickets</button>
            <button className="gold-button" onClick={handleAddTicket}>+ Add Ticket</button>
          </div>
        </div>
      </div>

      <div className="be-filters">
        <div className="range-select">
          <label>Range</label>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="input input-compact"
          >
            <option value="today">Today</option>
            <option value="last7">Last 7 Days</option>
            <option value="last30">Last 30 Days (default)</option>
          </select>
        </div>
        <div className="search-wrap">
          <input
            className="input input-compact"
            placeholder="Search any info (name, email, confirmation, etc.)"
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
          />
        </div>
      </div>

      <div className="be-card">
        <div className="be-table-wrap">
          <table className="be-table compact">
            <thead>
              <tr>
                <th>Confirmation</th>
                <th>First</th>
                <th>Last</th>
                <th>Email</th>
                <th>SMD/CEO</th>
                <th>QNT</th>
                <th>Price</th>
                <th>Total</th>
                <th>Invoice</th>
                <th>Reg Date</th>
              </tr>
            </thead>
            <tbody>
              {searched.map((p) => (
                <tr key={p.conf}>
                  <td className="mono">{p.conf}</td>
                  <td>{p.first}</td>
                  <td>{p.last}</td>
                  <td className="ellipsis">{p.email}</td>
                  <td>{p.leader}</td>
                  <td>{p.qnt}</td>
                  <td>${(Number(p.price || 0) / 100).toLocaleString()}</td>
                  <td>${(Number(p.total || 0) / 100).toLocaleString()}</td>
                  <td>
                    <div className="action-row">
                      <button className="chip chip-link" onClick={() => handleSendInvoice(p)}>Send Invoice</button>
                      <button className="chip" onClick={() => handleOpenPDF(p)}>Open PDF</button>
                    </div>
                  </td>
                  <td>{p.regDateTs ? new Date(p.regDateTs.seconds * 1000).toLocaleString() : ""}</td>
                </tr>
              ))}
              {searched.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty-cell">
                    No purchases found for your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCheckout && selectedEventForCheckout && (
        <div className="checkout-modal-overlay" onClick={() => setShowCheckout(false)}>
          <div className="checkout-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowCheckout(false)}>✕</button>
            <Elements stripe={stripePromise}>
              <EventCheckout
                eventId={selectedEventForCheckout.eventId}
                eventData={selectedEventForCheckout}
                ticketPrice={selectedEventForCheckout.price}
                onClose={() => setShowCheckout(false)}
                onSuccess={(data) => {
                  alert(`Payment successful! Confirmation: ${data.confirmationNumber}`);
                  setShowCheckout(false);
                }}
              />
            </Elements>
          </div>
        </div>
      )}
    </div>
  );
}
